import { useState } from 'react'
import { z } from 'zod'
import { useMutation } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { DUREE_LIEN_REINITIALISATION_MINUTES, schemaDemandeReinitialisation } from '@afs/shared'
import { Bandeau } from '../../../shared/ui/Bandeau.js'
import { Bouton } from '../../../shared/ui/Bouton.js'
import { Champ } from '../../../shared/ui/Champ.js'
import { estErreurApi } from '../../../shared/api/erreurs.js'
import { demanderReinitialisation } from '../api.js'
import styles from './Panneau.module.css'

/**
 * Demande d'un lien de réinitialisation.
 *
 * Cet écran existe pour qu'un oubli de mot de passe ne mobilise personne.
 * L'alternative — un administrateur qui regénère un mot de passe provisoire
 * et le transmet — a deux défauts : elle occupe quelqu'un d'autre, et elle
 * fait connaître le mot de passe à deux personnes.
 *
 * Le point de conception qui compte : **la confirmation est la même que
 * l'adresse existe ou non**. Le contraire ferait de cet écran le moyen le
 * plus simple d'obtenir la liste des comptes, en essayant
 * `prenom.nom@afs-smie.mg` en série (CCT § 5.1).
 */
export function MotDePasseOublie(): React.JSX.Element {
  const [email, setEmail] = useState('')
  const [erreurLocale, setErreurLocale] = useState<string | undefined>(undefined)

  const envoi = useMutation({ mutationFn: demanderReinitialisation })

  function soumettre(evenement: React.SyntheticEvent): void {
    evenement.preventDefault()

    const valide = schemaDemandeReinitialisation.safeParse({ email })
    if (!valide.success) {
      setErreurLocale(z.flattenError(valide.error).fieldErrors.email?.[0])
      return
    }

    setErreurLocale(undefined)
    envoi.mutate(valide.data)
  }

  if (envoi.isSuccess) {
    return (
      <div className={styles.ecran}>
        <div className={styles.carte}>
          <div className={styles.entete}>
            <h1 className={styles.titre}>Vérifiez votre messagerie</h1>
            <p className={styles.sousTitre}>
              Si un compte est associé à cette adresse, un lien vient d’y être envoyé. Il est
              valable {DUREE_LIEN_REINITIALISATION_MINUTES} minutes et ne fonctionne qu’une fois.
            </p>
          </div>

          {/*
            « Si un compte est associé » : la formulation est délibérée. Dire
            « un lien a été envoyé » confirmerait l'existence du compte.
          */}
          <p className={styles.mention}>
            Rien reçu au bout de quelques minutes&nbsp;? Regardez dans les indésirables, puis
            vérifiez l’adresse saisie — c’est votre adresse professionnelle qui sert d’identifiant.
          </p>

          <Link to="/connexion" className={styles.lien}>
            Revenir à la connexion
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.ecran}>
      <form className={styles.carte} onSubmit={soumettre} noValidate>
        <div className={styles.entete}>
          <h1 className={styles.titre}>Mot de passe oublié</h1>
          <p className={styles.sousTitre}>
            Indiquez votre adresse professionnelle : vous recevrez un lien pour en choisir un
            nouveau. Personne d’autre n’a à intervenir.
          </p>
        </div>

        {envoi.error && (
          <Bandeau
            ton="erreur"
            reference={estErreurApi(envoi.error) ? envoi.error.regle : undefined}
          >
            {envoi.error instanceof Error ? envoi.error.message : 'Demande impossible.'}
          </Bandeau>
        )}

        <Champ
          libelle="Adresse professionnelle"
          type="email"
          autoComplete="username"
          required
          autoFocus
          value={email}
          erreur={erreurLocale}
          onChange={(evenement) => {
            setEmail(evenement.target.value)
          }}
        />

        <Bouton type="submit" pleineLargeur enCours={envoi.isPending}>
          Recevoir le lien
        </Bouton>

        <Link to="/connexion" className={styles.lien}>
          Revenir à la connexion
        </Link>
      </form>
    </div>
  )
}
